import { ApiException } from "@/api/ResponseModel";
import SchoolInvitation from "@/models/APIModels/SchoolInvitation";
import { UserSecurityDetail } from "@/models/APIModels/UserSecurityDetail";
import SystemSetting from "@/models/SystemSetting";
import { ReferenceViewModel } from "@/models/ViewModels/ReferenceModel";
import { processResponse } from "@/shared/helperFunctions";
import ReferenceUseCase from "@/usecases/ReferenceUseCase";
import SystemSettingUseCase from "@/usecases/SystemSettingUseCase";
import { InjectionKey } from "vue";
import { createStore, useStore as baseStore, Store } from "vuex";

export interface State {
  referenceData: ReferenceViewModel;
  user?: UserSecurityDetail;
  newSchoolRegistration?: SchoolInvitation;
  systemSetting?: SystemSetting;
  messages: ApiException[];
  showLogoutDialog: boolean;
  logoutTimeInSeconds?: number;
}

export const key: InjectionKey<Store<State>> = Symbol();

const store = createStore<State>({
  state: {
    showLogoutDialog: false,

    referenceData: {
      businessSizeBrackets: [],
      sponsorshipLevels: [],
      userPermissions: [],
      sponsorshipFrequencies: [],
      donorTypes: [],
      userCategories: [],

      paymentSourceTypes: [],
      donorPaymentReconciliations: [],
      donorPaymentStatuses: [],
      userStatuses: [],
      australianStates: [],
      donorPaymentTypes: [],
      schoolStatuses: [],
      addressTypes: [],
      transactionTypes: [],

      reconciliationStatuses: [],
      reconciliationTypes: [],
      fundingRequestTypes: [],
      fundingRequestStatuses: [],
      fundingRequestClientStatuses: [],
      fundingPurposes: [],
      donationStatuses: [],

      paymentFrequencies: [],
      administratorRoles: [],
      schoolOfficerRoles: [],
      schoolOfficerDeclaration: [],
      registrationOfficerRoles: [],

      paymentStatuses: [],
      paymentTypes: [],
    },
    messages: [],
  },

  mutations: {
    mutStoreReferenceData(state, payload: ReferenceViewModel) {
      state.referenceData = payload;
    },
    mutStoreSystemSetting(state, payload) {
      state.systemSetting = payload;
    },
    mutStoreMessage(state, payload: ApiException) {
      // If messages already exists, append the new ones
      state.messages = [...state.messages, payload];
    },
    mutRemoveMessage(state, payload: string) {
      state.messages = state.messages.filter((item) => item.description !== payload);
    },
    mutStoreCurrentUser(state, payload?: UserSecurityDetail) {
      if (payload) state.user = payload;
    },
    mutClearCurrentUser(state) {
      state.user = undefined;
    },
    mutStoreNewSchoolRegistration(state, payload: SchoolInvitation) {
      state.newSchoolRegistration = payload;
    },
    mutToggleLogoutDialog(state, payload: boolean) {
      state.showLogoutDialog = payload;
    },
    mutStoreLogoutTimeInSeconds(state, payload?: number) {
      state.logoutTimeInSeconds = payload;
    },
  },
  actions: {
    async actionLoadReferenceData() {
      try {
        const referenceUseCase = new ReferenceUseCase();
        const result = await referenceUseCase.getAll();

        processResponse(result, (content) => {
          this.commit("mutStoreReferenceData", content);
        });
      } catch (error) {
        throw error;
      }
    },
    // async actionUpdateUser(_, user: UserSecurityDetail) {
    //   this.commit("mutStoreCurrentUser", user);

    // },
    async actionLoadSystemSetting() {
      try {
        const systemSettingUseCase = new SystemSettingUseCase();
        const result = await systemSettingUseCase.getAll();

        processResponse(result, (content) => {
          this.commit("mutStoreSystemSetting", content);
        });
      } catch (error) {
        throw error;
      }
    },
    actionToggleShowLogoutDialog(_, value: boolean) {
      console.log("actionToggleShowLogoutDialog...");
      this.commit("mutToggleLogoutDialog", value);
      console.log("...actionToggleShowLogoutDialog");
    },
  },
});

export function useStore() {
  return baseStore(key);
}

export default store;
